import * as React from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { useTheme, useMediaQuery } from '@mui/material'
import { Box, Typography, Chip, Avatar, Tooltip } from '@mui/material'
import { styled } from '@mui/material/styles'

// Simplified styled component with essential styling
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
	'& .MuiDataGrid-root': {
		border: 'none',
		borderRadius: theme.spacing(1),
		boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
	},
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: theme.palette.grey[50],
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	'& .MuiDataGrid-row': {
		'&:hover': {
			backgroundColor: theme.palette.action.hover,
		},
	},
	'& .MuiDataGrid-cell': {
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	// Basic row styling
	'& .even': {
		backgroundColor: theme.palette.grey[25],
	},
	'& .odd': {
		backgroundColor: theme.palette.background.paper,
	},
	// Mobile optimizations
	'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
		[theme.breakpoints.down('sm')]: {
			padding: theme.spacing(0.5, 1),
			fontSize: '0.8rem',
		},
	},
}))

// Essential cell renderers
const RankCell = ({ value }) => (
	<Chip
		label={`#${value}`}
		size="small"
		variant="outlined"
		sx={{
			fontWeight: 600,
			minWidth: '40px',
			height: '24px',
			fontSize: '0.75rem',
		}}
	/>
)

const ProfileImageCell = ({ value, row }) => (
	<Avatar
		src={value}
		alt={row.displayName || 'Profile'}
		sx={{ width: 32, height: 32 }}
	>
		{row.displayName?.charAt(0) || 'U'}
	</Avatar>
)

const EmailCell = ({ value }) => (
	<Tooltip title={value} placement="top">
		<Typography
			variant="body2"
			sx={{
				fontSize: '0.875rem',
				color: 'text.secondary',
				textOverflow: 'ellipsis',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
				maxWidth: '180px',
			}}
		>
			{value}
		</Typography>
	</Tooltip>
)

const AreaCell = ({ value }) => (
	<Chip
		label={value}
		size="small"
		variant="outlined"
		sx={{ fontSize: '0.75rem', height: '20px' }}
	/>
)

const CountCell = ({ value, field }) => {
	const getColor = (val, field) => {
		if (field === 'monthlyCount') {
			if (val >= 25) return 'success'
			if (val >= 15) return 'warning'
			return 'error'
		}
		return 'default'
	}

	return (
		<Chip
			label={value}
			size="small"
			color={getColor(value, field)}
			variant="filled"
			sx={{ fontWeight: 600, minWidth: '50px', height: '24px' }}
		/>
	)
}

export default function CustomizedDataGrid({
	rows = [],
	columns = [],
	onRowClick,
	loading = false,
}) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
	const isTablet = useMediaQuery(theme.breakpoints.down('md'))

	// Enhanced columns with essential cell renderers
	const enhancedColumns = React.useMemo(() => {
		return columns.map(col => {
			switch (col.field) {
				case 'serialNumber':
					return {
						...col,
						renderCell: (params) => <RankCell value={params.row.rank} />,
						width: isMobile ? 50 : 60,
						minWidth: isMobile ? 50 : 60,
					}
				case 'imageUrl':
					return {
						...col,
						renderCell: (params) => <ProfileImageCell value={params.value} row={params.row} />,
						width: isMobile ? 50 : 80,
						minWidth: isMobile ? 50 : 80,
					}
				case 'email':
					return {
						...col,
						renderCell: (params) => <EmailCell value={params.value} />,
						width: isMobile ? 120 : 200,
						minWidth: isMobile ? 120 : 200,
					}
				case 'area':
					return {
						...col,
						renderCell: (params) => <AreaCell value={params.value} />,
						width: isMobile ? 80 : 150,
						minWidth: isMobile ? 80 : 150,
					}
				case 'monthlyCount':
					return {
						...col,
						renderCell: (params) => <CountCell value={params.value} field="monthlyCount" />,
						width: isMobile ? 80 : 130,
						minWidth: isMobile ? 80 : 130,
					}
				case 'averageChantRounds':
				case 'averageHearDuration':
				case 'averageReadDuration':
				case 'averageServiceDuration':
					return {
						...col,
						renderCell: (params) => <CountCell value={params.value} field={col.field} />,
						width: isMobile ? 100 : 150,
						minWidth: isMobile ? 100 : 150,
					}
				default:
					return {
						...col,
						width: isMobile ? Math.min(col.minWidth || 100, 100) : col.minWidth || 150,
						minWidth: isMobile ? Math.min(col.minWidth || 100, 100) : col.minWidth || 150,
					}
			}
		})
	}, [columns, isMobile])

	// Responsive pagination settings
	const getPageSize = () => {
		if (isMobile) return 10
		if (isTablet) return 15
		return 20
	}

	const getPageSizeOptions = () => {
		if (isMobile) return [5, 10]
		if (isTablet) return [10, 15, 25]
		return [10, 20, 50]
	}

	// Basic row styling
	const getRowClassName = (params) => {
		return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
	}

	return (
		<Box sx={{
			width: '100%',
			height: 'auto',
			overflow: 'hidden',
			maxWidth: '100%',
		}}>
			<StyledDataGrid
				rows={rows}
				columns={enhancedColumns}
				loading={loading}
				autoHeight
				getRowClassName={getRowClassName}
				initialState={{
					pagination: {
						paginationModel: { pageSize: getPageSize() }
					},
				}}
				onRowClick={onRowClick}
				pageSizeOptions={getPageSizeOptions()}
				disableColumnResize
				disableColumnMenu={isMobile}
				disableColumnFilter={isMobile}
				disableColumnSelector={isMobile}
				disableDensitySelector={isMobile}
				density={isMobile ? "compact" : "standard"}
				rowHeight={isMobile ? 48 : 52}
				headerHeight={isMobile ? 48 : 56}
				sx={{
					width: '100%',
					maxWidth: '100%',
					overflow: 'hidden',
					'& .MuiDataGrid-main': {
						overflow: 'hidden',
					},
					'& .MuiDataGrid-virtualScroller': {
						overflow: 'hidden',
					},
					'& .MuiDataGrid-row': {
						cursor: 'pointer',
						'&:hover': {
							backgroundColor: theme.palette.action.hover,
						},
					},
				}}
				slotProps={{
					pagination: {
						labelRowsPerPage: isMobile ? 'Rows:' : 'Rows per page:',
						labelDisplayedRows: ({ from, to, count }) =>
							isMobile ? `${from}-${to} of ${count}` : `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`,
					},
					filterPanel: {
						filterFormProps: {
							logicOperatorInputProps: {
								variant: 'outlined',
								size: 'small',
							},
							columnInputProps: {
								variant: 'outlined',
								size: 'small',
								sx: { mt: 'auto' },
							},
							operatorInputProps: {
								variant: 'outlined',
								size: 'small',
								sx: { mt: 'auto' },
							},
							valueInputProps: {
								InputComponentProps: {
									variant: 'outlined',
									size: 'small',
								},
							},
						},
					},
				}}
			/>
		</Box>
	)
}
